import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { LabListItem, LabTopic } from '../../types/content';
import ContentBlocks from '../../components/content/ContentBlocks';
import TopicNavLinks from '../../components/content/TopicNavLinks';
import Button from '../../components/UI/Button';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { notifyLabCompleted } from '../../utils/labCompletion';
import PublicLabCourseNote from '../../components/content/PublicLabCourseNote';

const LabTopicPage: React.FC = () => {
  const { slug, topicSlug } = useParams();
  const [params] = useSearchParams();
  const catalogCourseSlug = params.get('course');
  const [topic, setTopic] = useState<LabTopic | null>(null);
  const [lab, setLab] = useState<LabListItem | null>(null);
  const [moduleTitle, setModuleTitle] = useState<string | null>(null);
  const [nextSlug, setNextSlug] = useState<string | null>(null);
  const [nextTitle, setNextTitle] = useState<string | null>(null);
  const [nextModuleTitle, setNextModuleTitle] = useState<string | null>(null);
  const [previousSlug, setPreviousSlug] = useState<string | null>(null);
  const [previousTitle, setPreviousTitle] = useState<string | null>(null);
  const [previousModuleTitle, setPreviousModuleTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalogCourseId, setCatalogCourseId] = useState<number | null>(null);
  const [labDone, setLabDone] = useState(false);
  const [isLastLabTopic, setIsLastLabTopic] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!slug || !topicSlug) return;
    setTopic(null);
    setLab(null);
    setError(null);
    setIsLastLabTopic(false);
    contentApi.getLabTopic(slug, topicSlug, catalogCourseSlug).then((r) => {
      if (!r.data.topic || !r.data.lab) {
        setError('This topic is not available.');
        return;
      }
      setTopic(r.data.topic);
      setLab(r.data.lab);
      setModuleTitle(r.data.moduleTitle || null);
      setNextSlug(r.data.nextTopicSlug || null);
      setIsLastLabTopic(!r.data.nextTopicSlug);
      setNextTitle(r.data.nextTopicTitle || null);
      setNextModuleTitle(r.data.nextModuleTitle || null);
      setPreviousSlug(r.data.previousTopicSlug || null);
      setPreviousTitle(r.data.previousTopicTitle || null);
      setPreviousModuleTitle(r.data.previousModuleTitle || null);
    }).catch(() => setError('This topic is not available.'));
  }, [slug, topicSlug, catalogCourseSlug]);

  useEffect(() => {
    if (!lab) return;
    const applyCourseId = (courseId: number) => {
      setCatalogCourseId(courseId);
      contentApi.getCourseProgress(courseId).then((p) => {
        setLabDone(!!p.data.progress?.completedLabIds?.includes(lab.id));
      }).catch(() => undefined);
    };
    if (lab.courseId) {
      applyCourseId(lab.courseId);
      return;
    }
    if (!catalogCourseSlug && !lab.courseSlug) return;
    contentApi.getCourseBySlug(catalogCourseSlug || lab.courseSlug || '').then((r) => {
      if (r.data.course?.id) applyCourseId(r.data.course.id);
    }).catch(() => undefined);
  }, [catalogCourseSlug, lab]);

  if (error) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">{error}</div>;
  if (!topic || !lab) return <PageLoading label="Loading topic…" />;

  const topicQuery = catalogCourseSlug ? `?course=${catalogCourseSlug}` : '';
  const toTopic = (path: string) => `/labs/${lab.slug}/topics/${path}${topicQuery}`;

  const markLabDone = () => {
    if (labDone || completing) return;
    if (!catalogCourseId) {
      setLabDone(true);
      notifyLabCompleted(lab.id, null);
      return;
    }
    setCompleting(true);
    contentApi.completeLab(catalogCourseId, lab.id)
      .then((response) => {
        setLabDone(!!response.data.progress?.completedLabIds?.includes(lab.id) || !!response.data.success);
        notifyLabCompleted(lab.id, catalogCourseId);
      })
      .catch(() => undefined)
      .finally(() => setCompleting(false));
  };

  return (
    <article>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <nav className="text-sm text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={catalogCourseSlug ? '/courses' : '/labs'} className="text-primary-600 font-medium hover:text-primary-800">{catalogCourseSlug ? 'Courses' : 'Labs'}</Link>
          {lab.courseTitle && catalogCourseSlug && (
            <>
              <span>/</span>
              <Link to={`/courses/${catalogCourseSlug}`} className="text-primary-600 font-medium hover:text-primary-800">{lab.courseTitle}</Link>
            </>
          )}
          <span>/</span>
          <Link to={`/labs/${lab.slug}${topicQuery}`} className="text-primary-600 font-medium hover:text-primary-800">{lab.name}</Link>
          {moduleTitle && (
            <>
              <span>/</span>
              <span>{moduleTitle}</span>
            </>
          )}
          <span>/</span>
          <span className="text-gray-800">{topic.title}</span>
        </nav>
        {moduleTitle && <p className="mt-4 text-xs uppercase tracking-wide text-gray-500">{moduleTitle}</p>}
        <h1 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900 inline-flex items-center gap-2">
          {topic.title}
          {labDone ? <CheckCircle className="h-7 w-7 text-success-600" aria-label="Completed" /> : null}
        </h1>
        {topic.objective && <p className="mt-4 text-gray-700">{topic.objective}</p>}
        {!catalogCourseSlug && lab.isPublic && (lab.courseSlug || lab.courseTitle) ? (
          <PublicLabCourseNote className="mt-4" courseSlug={lab.courseSlug} courseTitle={lab.courseTitle} />
        ) : null}

        <section className="mt-10">
          <ContentBlocks blocks={topic.blocks || []} compact />
        </section>

        <TopicNavLinks
          previousSlug={previousSlug}
          previousTitle={previousTitle}
          previousModuleTitle={previousModuleTitle}
          nextSlug={nextSlug}
          nextTitle={nextTitle}
          nextModuleTitle={nextModuleTitle}
          currentModuleTitle={moduleTitle}
          toTopic={toTopic}
        />

        {isLastLabTopic ? (
          <div className="mt-10">
            {labDone ? (
              <p className="inline-flex items-center gap-2 text-sm font-medium text-success-700">
                <CheckCircle className="h-4 w-4" /> Completed
              </p>
            ) : (
              <Button type="button" onClick={markLabDone} disabled={completing}>Done</Button>
            )}
          </div>
        ) : labDone ? (
          <p className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-success-700">
            <CheckCircle className="h-4 w-4" /> Completed
          </p>
        ) : null}
      </div>
    </article>
  );
};

export default LabTopicPage;
